// ====================================
//  Calendar annotations
// ====================================
//
//  Only hand-picked dates get callouts — not every cell. Add entries to
//  ANNOTATIONS below. Every callout is a compact "Read more" button until
//  clicked/tapped, then it expands to show its text (one open at a time;
//  click elsewhere or press Escape to collapse).
//
//  Desktop (months down the side, days across): the box sits above the
//  cell with a vertical connector. It anchors to the cell's exact x-position
//  (so the line always lands on the box) and grows toward the panel's
//  center (edge-aware).
//  Mobile (days down the side, months across): the box sits beside the cell
//  with a horizontal connector, on whichever side faces the grid's center,
//  and grows away from the cell.
//
//  Shape of each entry:
//    {
//      year: 2024,       // number — informational only; the panel sums all years
//      month: 10,        // 1–12, matches month_no in data.json
//      day: 5,           // matches day in data.json
//      text: '…',        // callout copy (plain text)
//      boxOffsetX: 0,    // optional desktop px — nudge left (−) / right (+)
//      boxOffsetY: 0,    // optional desktop px — negative moves box further up
//      mobileBoxOffsetX: 0, // optional mobile px — nudge the box left (−) / right (+). The
//                        //   connector line stretches to stay attached to the box.
//      mobileBoxOffsetY: 0, // optional mobile px — negative moves box up. Keep within
//                        //   about ±8 so the horizontal line still meets the box.
//    }
//
export const ANNOTATIONS = [
  {
    year: 2024,
    month: 10,
    day: 5,
    text: '2024: Three youth football games were disrupted by gun violence in a single day.',
  },
  {
    year: 2021,
    month: 10,
    day: 1,
    text: '2021: Two teenagers were shot and injured outside of football games at different schools.',
    boxOffsetY: -50,
    mobileBoxOffsetY: 4,
    mobileBoxOffsetX: -10,
  },
  {
    year: 2021,
    month: 8,
    day: 27,
    text: '2021: Three of the four shootings that happened on this date were associated with high school football games.',
  },
  // {
  //   year: 2016,
  //   month: 9,
  //   day: 2,
  //   text: '2016: Just one shooting was associated with a football game. In 2026, there have already been three.',
  //   mobileBoxOffsetY: 8,
  // },
];

const annotationsByDate = new Map(
  ANNOTATIONS.map(a => [`${a.year}-${a.month}-${a.day}`, a]),
);

export function getAnnotation(year, month, day) {
  return annotationsByDate.get(`${year}-${month}-${day}`);
}

export function getAllAnnotations() {
  return ANNOTATIONS
    .slice()
    .sort((a, b) => (a.month - b.month) || (a.day - b.day));
}

const SVG_NS = 'http://www.w3.org/2000/svg';
const BOX_GAP_ABOVE_CELL = 12;
const BOX_GAP_BESIDE_CELL = 10;
const READ_MORE_CUE = 'Read more';
const GRID_EDGE_INSET = 3;
// How far past the connector line's touch point a left/right-anchored
// box's near edge extends, so the line lands a few px inside the box
// instead of exactly on its corner.
const LINE_EDGE_INSET = 8;
const MIN_BOX_WIDTH = 72;
const COMPACT_MAX_WIDTH = 120;
const DESKTOP_EXPANDED_MAX_WIDTH = 260;
const ALIGN_LEFT_THRESHOLD = 0.33;
const ALIGN_RIGHT_THRESHOLD = 0.67;

const panelOverlayState = new WeakMap();

function chooseAlignment(cellCenterX, gridLeft, gridWidth) {
  const t = (cellCenterX - gridLeft) / gridWidth;
  if (t < ALIGN_LEFT_THRESHOLD) return 'left';
  if (t > ALIGN_RIGHT_THRESHOLD) return 'right';
  return 'center';
}

// Sizes the box from its content's natural (unwrapped) width, clamped to
// maxWidthPx, instead of the browser's default shrink-to-fit ('width:
// auto') behavior. Shrink-to-fit for an absolutely positioned box derives
// its "available width" from the CSS `left` offset and the containing
// block's right edge, with no awareness of the `transform: translate(-100%,
// ...)` trick used to flip boxes leftward -- so near the right edge it would
// silently shrink the box far below maxWidthPx. `max-content` sizes purely
// from content, so `max-width` clamps correctly.
function setContentFitWidth(box, maxWidthPx) {
  box.style.width = '-webkit-max-content'; // older WebKit; ignored if unsupported
  box.style.width = 'max-content';
  box.style.maxWidth = `${maxWidthPx}px`;
}

// Desktop: box above the cell. Anchors an edge of the box (or its center)
// directly to the cell's x-position -- the same x the connector line is
// drawn at -- then lets it grow toward the panel's center. Collapsed boxes
// are capped small; expanded boxes use the full remaining span, up to
// DESKTOP_EXPANDED_MAX_WIDTH.
function layoutVerticalBox(box, {
  gridLeft, gridRight, cellCenterX, cellTopY, offsetX, offsetY,
}) {
  const alignment = chooseAlignment(cellCenterX, gridLeft, gridRight - gridLeft);
  const isExpanded = box.classList.contains('annotation-box--expanded');
  box.dataset.layout = alignment;

  let availableSpan;
  if (alignment === 'left') {
    const anchorX = cellCenterX - LINE_EDGE_INSET + offsetX;
    box.style.left = `${anchorX}px`;
    box.style.transform = 'translate(0, -100%)';
    availableSpan = gridRight - GRID_EDGE_INSET - anchorX;
  } else if (alignment === 'right') {
    const anchorX = cellCenterX + LINE_EDGE_INSET + offsetX;
    box.style.left = `${anchorX}px`;
    box.style.transform = 'translate(-100%, -100%)';
    availableSpan = anchorX - (gridLeft + GRID_EDGE_INSET);
  } else {
    box.style.left = `${cellCenterX + offsetX}px`;
    box.style.transform = 'translate(-50%, -100%)';
    availableSpan = (2 * Math.min(
      cellCenterX - gridLeft,
      gridRight - cellCenterX,
    )) - (2 * GRID_EDGE_INSET);
  }

  availableSpan = Math.max(MIN_BOX_WIDTH, availableSpan);
  const maxWidth = isExpanded
    ? Math.min(availableSpan, DESKTOP_EXPANDED_MAX_WIDTH)
    : Math.min(availableSpan, COMPACT_MAX_WIDTH);
  setContentFitWidth(box, maxWidth);

  box.style.top = `${cellTopY - BOX_GAP_ABOVE_CELL + offsetY}px`;
}

// Mobile: box beside the cell, on the side facing the grid's center, with
// the connector running horizontally at the cell's center y. Expanded boxes
// grow away from the cell, favoring width over height.
function layoutHorizontalBox(box, {
  gridLeft, gridRight, cellLeft, cellRight, cellCenterY, offsetX, offsetY,
}) {
  const isExpanded = box.classList.contains('annotation-box--expanded');
  const gridCenter = (gridLeft + gridRight) / 2;
  const cellCenterX = (cellLeft + cellRight) / 2;
  const side = cellCenterX > gridCenter ? 'left' : 'right';
  box.dataset.layout = side;

  let availableSpan;
  if (side === 'left') {
    const edgeX = cellLeft - BOX_GAP_BESIDE_CELL + offsetX;
    box.style.left = `${edgeX}px`;
    box.style.transform = 'translate(-100%, -50%)';
    availableSpan = edgeX - (gridLeft + GRID_EDGE_INSET);
  } else {
    const edgeX = cellRight + BOX_GAP_BESIDE_CELL + offsetX;
    box.style.left = `${edgeX}px`;
    box.style.transform = 'translate(0, -50%)';
    availableSpan = (gridRight - GRID_EDGE_INSET) - edgeX;
  }

  availableSpan = Math.max(MIN_BOX_WIDTH, availableSpan);
  setContentFitWidth(box, isExpanded ? availableSpan : Math.min(availableSpan, COMPACT_MAX_WIDTH));

  box.style.top = `${cellCenterY + offsetY}px`;
}

function collapseCompactBox(box) {
  box.classList.remove('annotation-box--expanded');
  box.setAttribute('aria-expanded', 'false');
  box.textContent = READ_MORE_CUE;
}

function expandCompactBox(box) {
  const text = box.getAttribute('data-annotation-text');
  if (!text) return;

  box.classList.add('annotation-box--expanded');
  box.setAttribute('aria-expanded', 'true');
  box.textContent = text;
}

export function repositionPanelAnnotations(panelEl) {
  const state = panelOverlayState.get(panelEl);
  if (!state) return;
  positionAnnotationOverlays(panelEl, state.svg, state.overlays, state.isMobile);
}

function positionAnnotationOverlays(panelEl, svg, overlays, isMobile) {
  const containerRect = panelEl.getBoundingClientRect();
  svg.setAttribute('width', containerRect.width);
  svg.setAttribute('height', containerRect.height);

  const grid = panelEl.querySelector('.year-grid');
  let gridLeft = 0;
  let gridRight = containerRect.width;
  if (grid) {
    const gridRect = grid.getBoundingClientRect();
    gridLeft = gridRect.left - containerRect.left;
    gridRight = gridRect.right - containerRect.left;
  }

  overlays.forEach(({
    box, target, line, annotation,
  }) => {
    const targetRect = target.getBoundingClientRect();
    const cellLeft = targetRect.left - containerRect.left;
    const cellRight = targetRect.right - containerRect.left;
    const cellCenterX = cellLeft + (targetRect.width / 2);
    const cellTopY = targetRect.top - containerRect.top;
    const cellCenterY = cellTopY + (targetRect.height / 2);

    if (isMobile) {
      layoutHorizontalBox(box, {
        gridLeft,
        gridRight,
        cellLeft,
        cellRight,
        cellCenterY,
        offsetX: annotation.mobileBoxOffsetX || 0,
        offsetY: annotation.mobileBoxOffsetY || 0,
      });

      const boxRect = box.getBoundingClientRect();
      const boxIsLeft = box.dataset.layout === 'left';
      const boxEdgeX = (boxIsLeft ? boxRect.right : boxRect.left) - containerRect.left;
      line.setAttribute('x1', boxEdgeX);
      line.setAttribute('y1', cellCenterY);
      line.setAttribute('x2', boxIsLeft ? cellLeft : cellRight);
      line.setAttribute('y2', cellCenterY);
    } else {
      layoutVerticalBox(box, {
        gridLeft,
        gridRight,
        cellCenterX,
        cellTopY,
        offsetX: annotation.boxOffsetX || 0,
        offsetY: annotation.boxOffsetY || 0,
      });

      const boxRect = box.getBoundingClientRect();
      const boxBottomY = boxRect.bottom - containerRect.top;
      line.setAttribute('x1', cellCenterX);
      line.setAttribute('y1', boxBottomY);
      line.setAttribute('x2', cellCenterX);
      line.setAttribute('y2', cellTopY);
    }
  });
}

export function renderAnnotationOverlays(panelEl, isMobile) {
  panelEl.querySelectorAll('.annotation-box, .annotation-connectors').forEach(el => el.remove());

  const annotations = getAllAnnotations();
  if (!annotations.length) return;

  const grid = panelEl.querySelector('.year-grid');
  if (!grid) return;

  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('class', 'annotation-connectors');
  svg.setAttribute('aria-hidden', 'true');
  panelEl.appendChild(svg);

  const overlays = [];

  annotations.forEach((annotation) => {
    const target = grid.querySelector(
      `.cell[data-month="${annotation.month}"][data-day="${annotation.day}"]`,
    );
    if (!target) return;

    target.classList.add('cell--annotated');

    const box = document.createElement('button');
    box.type = 'button';
    box.className = 'annotation-box annotation-box--compact';
    box.setAttribute('data-annotation-text', annotation.text);
    box.setAttribute('aria-expanded', 'false');
    box.setAttribute(
      'aria-label',
      `Annotation for ${annotation.month}/${annotation.day}: ${READ_MORE_CUE}`,
    );
    box.textContent = READ_MORE_CUE;
    panelEl.appendChild(box);

    const line = document.createElementNS(SVG_NS, 'line');
    line.setAttribute('class', 'annotation-connector');
    svg.appendChild(line);

    overlays.push({
      box, target, line, annotation,
    });
  });

  panelOverlayState.set(panelEl, { svg, overlays, isMobile });

  positionAnnotationOverlays(panelEl, svg, overlays, isMobile);

  const resizeObserver = new ResizeObserver(() => {
    positionAnnotationOverlays(panelEl, svg, overlays, isMobile);
  });
  resizeObserver.observe(panelEl);
}

function collapseAllCompactBoxes(root) {
  const panels = new Set();
  root.querySelectorAll('.annotation-box--compact.annotation-box--expanded').forEach((box) => {
    const panel = box.closest('.year-panel');
    if (panel) panels.add(panel);
    collapseCompactBox(box);
  });
  panels.forEach(panel => repositionPanelAnnotations(panel));
}

export function initAnnotationExpand(root) {
  root.addEventListener('click', (e) => {
    const compact = e.target.closest('.annotation-box--compact');
    if (compact) {
      e.stopPropagation();
      const panel = compact.closest('.year-panel');
      const wasExpanded = compact.classList.contains('annotation-box--expanded');

      root.querySelectorAll('.annotation-box--compact.annotation-box--expanded').forEach((box) => {
        if (box !== compact) {
          const otherPanel = box.closest('.year-panel');
          collapseCompactBox(box);
          if (otherPanel) repositionPanelAnnotations(otherPanel);
        }
      });

      if (wasExpanded) {
        collapseCompactBox(compact);
      } else {
        expandCompactBox(compact);
      }

      if (panel) repositionPanelAnnotations(panel);
      return;
    }

    collapseAllCompactBoxes(root);
  });
}

if (typeof document !== 'undefined' && !document.documentElement.dataset.annotationEscapeBound) {
  document.documentElement.dataset.annotationEscapeBound = '1';
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('.calendar-graphic').forEach((graphic) => {
      collapseAllCompactBoxes(graphic);
    });
  });
}
